import React, { useCallback } from "react";
import { Counterparty } from "@common/types/counterparty";
import {
    useCopyGlobalCounterpartyToLocalMutation,
    useUpdateCounterpartyMutation,
} from "@api/counterparty.api";
import {
    CounterpartyForm,
    CounterpartyFormFields,
    CounterpartyFormSubmission,
    CounterpartyFormSubmitHelpers,
} from "../../counterparty/CounterpartyForm";
import { toCompanyCounterpartyUpdate } from "../../counterparty/counterpartyFormSchema";

interface Props {
    counterparty: Counterparty;
    onEdited: (updatedCounterparty: Counterparty) => void;
    onCancelled: () => void;
    fullWidth?: boolean;
    sectionClassName?: string;
    fields?: CounterpartyFormFields;
    dirtyRef?: React.MutableRefObject<boolean>;
    onSubmittingChange?: (isSubmitting: boolean) => void;
    formId?: string;
    footer?: "form" | "none";
    betweenBasicInfoAndTax?: React.ReactNode;
}

export const EditCounterparty: React.FC<Props> = ({
    counterparty,
    onEdited,
    onCancelled,
    fullWidth,
    sectionClassName,
    fields = "full",
    dirtyRef,
    onSubmittingChange,
    formId,
    footer,
    betweenBasicInfoAndTax,
}) => {
    const updateCounterparty = useUpdateCounterpartyMutation(counterparty);
    const copyGlobalToLocal = useCopyGlobalCounterpartyToLocalMutation();
    // onEdited can close the drawer, which unmounts the tax ID field, so the
    // tax ID is saved before it runs.
    const onSubmit = useCallback(
        async (
            data: CounterpartyFormSubmission,
            { saveTin, saveTinOrNotify }: CounterpartyFormSubmitHelpers,
        ) => {
            const payload =
                fields === "full" ? data : toCompanyCounterpartyUpdate(data);
            if (counterparty.workspaceId) {
                const isTinSaved = await saveTin(counterparty);
                if (!isTinSaved) {
                    throw new Error("Failed to save tax ID");
                }
                onEdited(await updateCounterparty.mutateAsync(payload));
                return;
            }
            const localCounterparty = await copyGlobalToLocal.mutateAsync({
                globalCounterpartyId: counterparty.id,
                update: payload,
            });
            await saveTinOrNotify(localCounterparty, "Updated");
            onEdited(localCounterparty);
        },
        [updateCounterparty, copyGlobalToLocal, counterparty, fields, onEdited],
    );

    return (
        <CounterpartyForm
            counterparty={counterparty}
            onSubmit={onSubmit}
            onCancelled={onCancelled}
            submitText="Save"
            fullWidth={fullWidth}
            sectionClassName={sectionClassName}
            fields={fields}
            dirtyRef={dirtyRef}
            onSubmittingChange={onSubmittingChange}
            formId={formId}
            footer={footer}
            betweenBasicInfoAndTax={betweenBasicInfoAndTax}
        />
    );
};
